import { useState, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "~/components/ui/sheet";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Plus, Trash2, Loader2, AlertTriangle, Undo2 } from "lucide-react";
import { toast } from "sonner";

interface EditEnumDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schema: string;
  enumName: string;
  initialValues: string[];
  onSuccess: () => void;
}

export function EditEnumDialog({
  open,
  onOpenChange,
  schema,
  enumName,
  initialValues,
  onSuccess,
}: EditEnumDialogProps) {
  const [existingValues, setExistingValues] = useState<string[]>([]);
  const [removedValues, setRemovedValues] = useState<Set<string>>(new Set());
  const [newValues, setNewValues] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when dialog opens or initialValues change
  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setExistingValues([...initialValues]);
      setRemovedValues(new Set());
      setNewValues([]);
      setError(null);
    }
  }, [open, initialValues]);

  function addNewValue() {
    setNewValues((prev) => [...prev, ""]);
  }

  function removeNewValue(index: number) {
    setNewValues((prev) => prev.filter((_, i) => i !== index));
  }

  function updateNewValue(index: number, value: string) {
    setNewValues((prev) => prev.map((v, i) => (i === index ? value : v)));
  }

  function toggleRemoveExisting(value: string) {
    setRemovedValues((prev) => {
      const next = new Set(prev);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
  }

  const hasRemovals = removedValues.size > 0;
  const cleanNewValues = newValues.map((v) => v.trim()).filter(Boolean);
  const hasChanges = cleanNewValues.length > 0 || hasRemovals;

  async function handleSubmit() {
    setError(null);

    if (!hasChanges) {
      setError("No changes to save");
      return;
    }

    // Validate new values
    const allExisting = existingValues.filter((v) => !removedValues.has(v));
    const allValues = [...allExisting, ...cleanNewValues];

    const uniqueValues = new Set(allValues);
    if (uniqueValues.size !== allValues.length) {
      setError("Duplicate values are not allowed");
      return;
    }

    for (const v of cleanNewValues) {
      if (existingValues.includes(v)) {
        setError(`Value "${v}" already exists in this enum`);
        return;
      }
    }

    setSaving(true);

    try {
      if (hasRemovals) {
        // Drop + recreate approach: rename old type, create new type with
        // remaining values, update all columns using the old type, drop old type.
        const keepValues = existingValues
          .filter((v) => !removedValues.has(v))
          .concat(cleanNewValues);

        if (keepValues.length === 0) {
          setError("Cannot remove all values from an enum. Delete the enum instead.");
          setSaving(false);
          return;
        }

        const escapedName = `"${enumName}"`;
        const tempName = `"${enumName}__old"`;
        const valuesList = keepValues.map((v) => `'${v.replace(/'/g, "''")}'`).join(", ");

        // Build a multi-statement transaction:
        // 1. Rename existing type to temporary name
        // 2. Create new type with the desired values
        // 3. Update all columns that use the old type to the new type
        // 4. Drop the old (renamed) type
        const sql = [
          `ALTER TYPE ${escapedName} RENAME TO "${enumName}__old"`,
          `CREATE TYPE ${escapedName} AS ENUM (${valuesList})`,
          // Update all columns referencing the old type
          `DO $$
DECLARE
  r RECORD;
BEGIN
  FOR r IN
    SELECT c.table_schema, c.table_name, c.column_name
    FROM information_schema.columns c
    WHERE c.udt_name = '${enumName.replace(/'/g, "''")}__old'
      AND c.table_schema = current_schema()
  LOOP
    EXECUTE format(
      'ALTER TABLE %I.%I ALTER COLUMN %I TYPE ${escapedName} USING %I::text::${escapedName}',
      r.table_schema, r.table_name, r.column_name, r.column_name
    );
  END LOOP;
END $$`,
          `DROP TYPE ${tempName}`,
        ].join(";\n");

        const res = await fetch("/api/database/execute", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sql }),
        });

        const data = await res.json();
        if (!res.ok) {
          setError(data.error || "Failed to update enum. Values in use cannot be removed.");
          setSaving(false);
          return;
        }
      } else {
        // Only adding new values: use ALTER TYPE ADD VALUE for each
        for (const val of cleanNewValues) {
          const escaped = val.replace(/'/g, "''");
          const sql = `ALTER TYPE "${enumName}" ADD VALUE '${escaped}'`;

          const res = await fetch("/api/database/execute", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ sql }),
          });

          const data = await res.json();
          if (!res.ok) {
            setError(data.error || `Failed to add value "${val}"`);
            setSaving(false);
            return;
          }
        }
      }

      onSuccess();
      onOpenChange(false);
      toast.success(`Enum "${enumName}" updated`);
    } catch (err: any) {
      setError(err.message);
    }
    setSaving(false);
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[420px] sm:w-[480px]">
        <SheetHeader>
          <SheetTitle>Edit Enum Type</SheetTitle>
          <SheetDescription>
            Modify values for{" "}
            <span className="font-mono text-foreground">{enumName}</span>{" "}
            in the <span className="font-mono text-foreground">{schema}</span> schema.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {/* Enum Name (read-only) */}
          <div className="space-y-1.5">
            <Label className="text-sm">Enum Name</Label>
            <div className="h-8 flex items-center px-3 rounded-md border bg-muted/50 text-sm font-mono text-muted-foreground">
              {enumName}
            </div>
          </div>

          {/* Existing Values */}
          <div className="space-y-1.5">
            <Label className="text-sm">Current Values</Label>
            <div className="space-y-1.5">
              {existingValues.map((value, i) => {
                const isRemoved = removedValues.has(value);
                return (
                  <div key={`existing-${i}`} className="flex items-center gap-1.5">
                    <span className="text-sm text-muted-foreground w-5 text-right tabular-nums">
                      {i + 1}
                    </span>
                    <div
                      className={`h-7 flex items-center px-2 rounded-md border text-sm font-mono flex-1 ${
                        isRemoved
                          ? "line-through text-muted-foreground/50 bg-destructive/5 border-destructive/20"
                          : "bg-muted/30"
                      }`}
                    >
                      {value}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`h-7 w-7 p-0 ${
                        isRemoved
                          ? "text-muted-foreground hover:text-foreground"
                          : "text-muted-foreground hover:text-destructive"
                      }`}
                      onClick={() => toggleRemoveExisting(value)}
                      title={isRemoved ? "Restore value" : "Mark for removal"}
                    >
                      {isRemoved ? (
                        <Undo2 className="h-3 w-3" />
                      ) : (
                        <Trash2 className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Removal Warning */}
          {hasRemovals && (
            <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
              <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
              <div className="text-xs text-amber-600 dark:text-amber-400">
                <p className="font-medium">Removing enum values requires recreating the type.</p>
                <p className="mt-1">
                  This will fail if any removed values are currently stored in table columns.
                  All columns using this enum will be updated automatically.
                </p>
              </div>
            </div>
          )}

          {/* New Values */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-sm">New Values</Label>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 text-sm gap-1"
                onClick={addNewValue}
              >
                <Plus className="h-3 w-3" />
                Add Value
              </Button>
            </div>
            {newValues.length === 0 ? (
              <p className="text-xs text-muted-foreground py-1">
                No new values added yet.
              </p>
            ) : (
              <div className="space-y-1.5">
                {newValues.map((value, i) => (
                  <div key={`new-${i}`} className="flex items-center gap-1.5">
                    <span className="text-sm text-muted-foreground w-5 text-right tabular-nums">
                      {existingValues.length + i + 1}
                    </span>
                    <Input
                      value={value}
                      onChange={(e) => updateNewValue(i, e.target.value)}
                      placeholder={`new_value_${i + 1}`}
                      className="h-7 text-sm font-mono flex-1"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addNewValue();
                        }
                      }}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                      onClick={() => removeNewValue(i)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && (
            <p className="text-xs text-destructive">{error}</p>
          )}
        </div>

        <SheetFooter className="mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving || !hasChanges}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save Changes
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
